import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, BookOpen, Sparkles } from "lucide-react";

// Colour-strand per class band — same tokens used across the app so the
// KA-style hierarchy feels native, not bolted on.
const BAND_STYLES = {
  "6":  "from-pink-300/10 via-orange-300/5 to-transparent",
  "7":  "from-orange-300/10 via-pink-300/5 to-transparent",
  "8":  "from-yellow-300/10 via-orange-300/5 to-transparent",
  "9":  "from-sky-300/10 via-yellow-300/5 to-transparent",
  "10": "from-emerald-300/10 via-sky-300/5 to-transparent",
  "11": "from-violet-300/10 via-pink-300/5 to-transparent",
  "12": "from-fuchsia-300/10 via-violet-300/5 to-transparent",
};

function CourseCard({ course }) {
  const band = BAND_STYLES[course.grade] || "from-white/5 to-transparent";
  const isComingSoon = !course.practice_bank_ready;
  return (
    <Link
      to={`/learn/${course.id}`}
      data-testid={`learn-course-${course.id}`}
      className={`fade-up lift rounded-lg border border-white/10 bg-[#152040] p-6 relative overflow-hidden flex flex-col group transition-colors hover:border-yellow-300/40`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${band} pointer-events-none`} />
      <div className="relative flex flex-col h-full">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">
            CBSE · Class {course.grade}
          </div>
          {course.difficulty && (
            <div className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border ${
              course.difficulty === "standard"
                ? "border-emerald-300/40 text-emerald-300 bg-emerald-300/5"
                : "border-sky-300/40 text-sky-300 bg-sky-300/5"
            }`}>
              {course.difficulty === "standard" ? "041 · Standard" : "241 · Basic"}
            </div>
          )}
        </div>
        <div className="font-display text-2xl font-bold mb-2 leading-snug">{course.title}</div>
        <div className="text-sm text-zinc-400 mb-5 min-h-[40px]">{course.description}</div>
        <div className="mt-auto flex items-center justify-between pt-3 border-t border-white/5">
          {isComingSoon ? (
            <div className="text-[11px] uppercase tracking-[0.15em] text-orange-300 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Practice bank in progress
            </div>
          ) : (
            <div className="text-[11px] uppercase tracking-[0.15em] text-emerald-300 flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5" /> Full practice unlocked
            </div>
          )}
          <div className="text-yellow-300 flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all">
            Open <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Learn() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/learn/courses").then((r) => {
      setCourses(r.data.courses || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Group by grade: 10 has two tracks, so we render them side by side.
  const grades = ["6", "7", "8", "9", "10", "11", "12"];
  const byGrade = grades.reduce((acc, g) => {
    acc[g] = courses.filter((c) => c.grade === g);
    return acc;
  }, {});

  return (
    <div data-testid="learn-page">
      <div className="mb-8 fade-up flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-2">Learn</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
            Every course, broken into units and skills
          </h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">
            Pick a class, dive into a unit, and work through lessons one skill at a time — the way you'd walk a friend through a topic, not a textbook.
          </p>
        </div>
        <div
          className="text-[11px] uppercase tracking-[0.2em] text-pink-300 border border-pink-300/30 rounded-full px-3 py-1"
          data-testid="learn-page-badge"
        >
          NCERT 2026-27
        </div>
      </div>

      {loading && (
        <div className="text-zinc-500 text-sm" data-testid="learn-loading">Loading courses…</div>
      )}

      {!loading && (
        <div className="space-y-8">
          {grades.map((g) => {
            const cs = byGrade[g];
            if (!cs?.length) return null;
            return (
              <section key={g} data-testid={`learn-grade-${g}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="font-display text-lg text-zinc-300">Class {g}</div>
                  <div className="h-px flex-1 bg-white/5" />
                  {g === "10" && (
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                      Two tracks · pick your board paper
                    </div>
                  )}
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {cs.map((c) => <CourseCard key={c.id} course={c} />)}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
